/*
  <cart-drawer> — slide-in cart for the alife theme.

  Responsibilities:
    - Open when an item is added to cart (intercepts any `/cart/add` form submit)
      and when the header cart link is clicked.
    - Update line quantities / remove lines via the Cart Ajax API.
    - Re-render this section's contents via the Section Rendering API after every
      cart mutation, so prices, the free-shipping bar and the empty/full state
      stay correct without a full reload.
    - Dispatch `cart:updated` so the header count (and anything else) stays live.

  Degrades gracefully: without JS the cart icon links to /cart, add-to-cart forms
  post normally, and the checkout button submits the native cart form.
*/
(function () {
  if (customElements.get('cart-drawer')) return;

  var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function cartUrl(path) {
    return (routes + path).replace(/\/\/+/g, '/');
  }

  customElements.define('cart-drawer', class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.overlay = this.querySelector('[data-cart-overlay]');
      this.panel = this.querySelector('.cart-drawer__panel');
      this.cursorEl = this.querySelector('[data-cart-cursor]');
      this.lastFocused = null;
      this.noteTimer = null;

      // Make this instance reachable from the document-level interceptors.
      CartDrawer.instance = this;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      document.addEventListener('keydown', this.onKeydown.bind(this));

      this.bindGlobal();
      this.setupCursor();
      this.setupGesture();
    }

    /* Swipe the header down to dismiss the bottom sheet (mobile). The header
       lives inside [data-cart-content], which the Section Rendering API
       replaces wholesale after every cart mutation — so this re-runs on swap. */
    setupGesture() {
      this.gesture = this.querySelector('gesture-element');
      if (!this.gesture || !this.gesture.enableDismiss) return;
      var self = this;
      this.gesture.enableDismiss({
        wrap: this.panel,
        overlay: this.overlay,
        isOpen: function () { return self.hasAttribute('open'); },
        onDismiss: function () { self.close(); }
      });
    }

    /* ---- Concept-style follow cursor over the backdrop ----
       A circular "×" eases toward the pointer, grows while over the overlay
       (.is-active) and scales up on press (.is-pressed). Unlike Concept (which
       eases inside the mousemove handler), we ease in a rAF loop so the circle
       always catches up to the live pointer — even when the mouse stops — so it
       never gets stranded mid-lag when you click to close. */
    setupCursor() {
      if (!this.cursorEl) return;
      // Hover-capable, fine pointers only — matches the `cursor: none` CSS guard.
      if (!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)) return;

      this.cursorEnabled = true;
      this.cursorTarget = { x: 0, y: 0 };
      this.cursorPos = { x: 0, y: 0 };
      this.cursorSeeded = false;
      this.cursorRAF = null;

      var self = this;
      document.addEventListener('mousemove', function (event) {
        self.cursorTarget.x = event.clientX;
        self.cursorTarget.y = event.clientY;
        if (!self.cursorSeeded) {
          // First sighting: drop the circle straight onto the pointer.
          self.cursorPos.x = event.clientX;
          self.cursorPos.y = event.clientY;
          self.cursorSeeded = true;
        }
      });

      var cursor = this.cursorEl;
      if (this.overlay) {
        // Over the backdrop the × cursor takes over — hide the site-wide ink
        // trail (.ink-hidden on <html>) so the two never overlap.
        var activate = function () { cursor.classList.add('is-active'); document.documentElement.classList.add('ink-hidden'); };
        this.overlay.addEventListener('mouseenter', activate);
        this.overlay.addEventListener('mousemove', activate);
        this.overlay.addEventListener('mouseleave', function () { cursor.classList.remove('is-active', 'is-pressed'); document.documentElement.classList.remove('ink-hidden'); });
        this.overlay.addEventListener('mousedown', function () { cursor.classList.add('is-pressed'); });
        this.overlay.addEventListener('mouseup', function () { cursor.classList.remove('is-pressed'); });
      }
    }

    /* rAF easing loop — runs only while the drawer is open. */
    startCursorLoop() {
      if (!this.cursorEnabled || this.cursorRAF) return;
      var self = this;
      var step = function () {
        // Ease ~20% per frame toward the live pointer; snap when basically there.
        self.cursorPos.x += (self.cursorTarget.x - self.cursorPos.x) * 0.2;
        self.cursorPos.y += (self.cursorTarget.y - self.cursorPos.y) * 0.2;
        self.cursorEl.style.setProperty('--cursor-x', self.cursorPos.x + 'px');
        self.cursorEl.style.setProperty('--cursor-y', self.cursorPos.y + 'px');
        self.cursorRAF = requestAnimationFrame(step);
      };
      this.cursorRAF = requestAnimationFrame(step);
    }

    stopCursorLoop() {
      if (this.cursorRAF) { cancelAnimationFrame(this.cursorRAF); this.cursorRAF = null; }
    }

    /* Document-level interceptors — bound once across the page lifetime. */
    bindGlobal() {
      if (CartDrawer.globalBound) return;
      CartDrawer.globalBound = true;

      // Add-to-cart: any form posting to /cart/add → AJAX add + open drawer.
      document.addEventListener('submit', function (event) {
        var form = event.target.closest && event.target.closest('form[action*="/cart/add"], form[action$="/cart/add"]');
        if (!form) return;
        var instance = CartDrawer.instance;
        if (!instance) return;
        event.preventDefault();
        instance.addFromForm(form);
      });

      // Header cart link → open the drawer instead of navigating to /cart.
      document.addEventListener('click', function (event) {
        var link = event.target.closest && event.target.closest('a[href]');
        if (!link) return;
        var instance = CartDrawer.instance;
        if (!instance) return;
        var url;
        try { url = new URL(link.href, window.location.origin); } catch (e) { return; }
        if (url.origin !== window.location.origin) return;
        if (!/\/cart\/?$/.test(url.pathname)) return;
        if (url.search) return; // permalinks like /cart/clear keep their default behaviour
        event.preventDefault();
        instance.open();
        instance.refresh();
      });
    }

    /* ---- Open / close ---- */
    open() {
      if (this.hasAttribute('open')) return;
      this.lastFocused = document.activeElement;
      this.hidden = false;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-drawer-locked');
      this.startCursorLoop();
      // Force the browser to commit the off-screen start state (translateX(100%))
      // before flipping [active]. A single rAF isn't enough on Firefox (esp.
      // mobile), where the style recalc after display:none→block is coalesced
      // into the same frame, so the panel jumps straight to its final position
      // with no transition. Reading a layout property flushes that start style.
      if (this.panel) void this.panel.offsetWidth; // eslint-disable-line no-unused-expressions
      requestAnimationFrame(function () {
        this.setAttribute('active', '');
        if (this.panel) this.panel.focus({ preventScroll: true });
      }.bind(this));
    }

    close() {
      if (!this.hasAttribute('open')) return;
      if (this.gesture && this.gesture.resetDismiss) this.gesture.resetDismiss();
      this.removeAttribute('active');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-drawer-locked');
      if (this.cursorEl) this.cursorEl.classList.remove('is-active', 'is-pressed');
      document.documentElement.classList.remove('ink-hidden');

      var finish = function () {
        this.removeAttribute('open');
        this.hidden = true;
        this.stopCursorLoop();
        if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus({ preventScroll: true });
      }.bind(this);

      if (this.prefersReducedMotion()) { finish(); return; }
      var done = false;
      var onEnd = function (e) {
        if (e.target !== this.panel) return;
        done = true;
        this.panel.removeEventListener('transitionend', onEnd);
        finish();
      }.bind(this);
      this.panel.addEventListener('transitionend', onEnd);
      // Fallback in case transitionend doesn't fire — must outlast the 600ms slide.
      setTimeout(function () { if (!done) { if (this.panel) this.panel.removeEventListener('transitionend', onEnd); finish(); } }.bind(this), 800);
    }

    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* ---- Event delegation inside the drawer ---- */
    onClick(event) {
      if (event.target.closest('[data-cart-close]')) { this.close(); return; }
      if (event.target === this.overlay) { this.close(); return; }

      // Tool-sheet × and scrim both carry data-cart-panel-close.
      var panelClose = event.target.closest('[data-cart-panel-close]');
      if (panelClose) {
        var openPanel = panelClose.closest('[data-cart-panel]');
        if (openPanel) this.togglePanel(openPanel.dataset.cartPanel);
        return;
      }

      var panelToggle = event.target.closest('[data-cart-panel-toggle]');
      if (panelToggle) { this.togglePanel(panelToggle.dataset.cartPanelToggle); return; }

      var apply = event.target.closest('[data-cart-discount-apply]');
      if (apply) { this.applyDiscount(); return; }

      var removeCode = event.target.closest('[data-cart-discount-remove]');
      if (removeCode) {
        var chip = removeCode.closest('[data-discount-code]');
        if (chip) this.removeDiscount(chip.dataset.discountCode);
        return;
      }

      var line = event.target.closest('[data-cart-line]');
      if (!line) return;
      var key = line.dataset.lineKey;
      var current = parseInt(line.querySelector('[data-qty-value]').textContent, 10) || 0;

      if (event.target.closest('[data-qty-up]')) { this.changeLine(key, current + 1); return; }
      if (event.target.closest('[data-qty-down]')) { this.changeLine(key, current - 1); return; }
      if (event.target.closest('[data-cart-remove]')) { this.changeLine(key, 0); return; }
    }

    onInput(event) {
      // Consent checkboxes: persist immediately so the choice survives the
      // Section Rendering re-renders and lands on the order even if the
      // visitor pays from a later session.
      if (event.target.matches('[data-cart-consent]')) {
        this.saveConsent(event.target);
        return;
      }
      if (!event.target.matches('[data-cart-note]')) return;
      clearTimeout(this.noteTimer);
      var value = event.target.value;
      this.noteTimer = setTimeout(function () { this.saveNote(value); }.bind(this), 500);
    }

    saveConsent(input) {
      var match = /^attributes\[(.+)\]$/.exec(input.name || '');
      if (!match) return;
      var attributes = {};
      attributes[match[1]] = input.checked ? input.value || 'Taip' : '';
      fetch(cartUrl('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).catch(function () {});
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.hasAttribute('open')) {
        // A tool sheet is a layer of its own — Escape peels it off first.
        var activePanel = this.querySelector('[data-cart-panel][active]');
        if (activePanel) { this.togglePanel(activePanel.dataset.cartPanel); return; }
        this.close();
        return;
      }
      // The discount input sits inside the cart <form>, so a bare Enter would
      // submit it — i.e. jump to checkout. Apply the code instead.
      // This listener is document-level, and on /cart the page's own discount
      // field matches the same selector — so only act on our own input, or the
      // closed drawer would steal focus from <cart-page>.
      if (event.key !== 'Enter') return;
      if (!event.target.matches || !event.target.matches('[data-cart-discount]')) return;
      if (!this.contains(event.target)) return;
      event.preventDefault();
      this.applyDiscount();
    }

    togglePanel(name) {
      var self = this;
      var panels = Array.prototype.slice.call(this.querySelectorAll('[data-cart-panel]'));
      var target = panels.filter(function (p) { return p.dataset.cartPanel === name; })[0];
      if (!target) return;

      // [active], not `hidden`, is the source of truth: while a sheet is mid-
      // slide-out it's no longer active but not yet re-hidden, and toggling
      // then should bring it back.
      var willOpen = !target.hasAttribute('active');

      panels.forEach(function (panel) {
        if (panel === target) {
          self.animatePanel(panel, willOpen);
        } else if (!panel.hidden) {
          self.animatePanel(panel, false); // one sheet at a time
        }
      });

      // Highlight the active toggle.
      this.querySelectorAll('[data-cart-panel-toggle]').forEach(function (btn) {
        btn.classList.toggle('is-active', willOpen && btn.dataset.cartPanelToggle === name);
      });
    }

    /* Concept-style slide-over for the note / shipping / discount sheets:
       [active] drives the CSS transitions (scrim fade + sheet slide) and
       `hidden` keeps closed panels out of the layout. The layout read commits
       the off-screen start state before [active] flips — same flush trick as
       open(). */
    animatePanel(panel, open) {
      clearTimeout(panel._cartPanelHide);

      if (this.prefersReducedMotion()) {
        panel.hidden = !open;
        panel.toggleAttribute('active', open);
        if (open) this.focusPanel(panel);
        return;
      }

      if (open) {
        panel.hidden = false;
        void panel.offsetWidth; // eslint-disable-line no-unused-expressions
        panel.setAttribute('active', '');
        this.focusPanel(panel);
      } else {
        panel.removeAttribute('active');
        // Re-hide after the 600ms slide; a prompt reopen cancels this timer.
        panel._cartPanelHide = setTimeout(function () { panel.hidden = true; }, 650);
      }
    }

    focusPanel(panel) {
      // Shipping has no field — land focus on the sheet's × instead.
      var field = panel.querySelector('textarea, input') || panel.querySelector('.cart-panel__close');
      if (field) setTimeout(function () { field.focus({ preventScroll: true }); }, 60);
    }

    /* ---- Cart Ajax API ---- */
    addFromForm(form) {
      var formData = new FormData(form);
      formData.append('sections', this.sectionId);
      formData.append('sections_url', window.location.pathname);

      this.setBusy(true);
      var submitButton = form.querySelector('[type="submit"]');
      var loader = window.theme && window.theme.buttonLoader;
      if (loader) loader.start(submitButton);
      else if (submitButton) submitButton.setAttribute('aria-disabled', 'true');

      fetch(cartUrl('cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data && data.status) { this.setBusy(false); return; } // error (e.g. sold out)
          this.renderSections(data && data.sections);
          this.open();
        }.bind(this))
        .catch(function () {
          // Network failure → fall back to a native submit.
          form.submit();
        })
        .finally(function () {
          this.setBusy(false);
          if (submitButton) submitButton.removeAttribute('aria-disabled');
          // stopAll also clears the sticky-bar proxy button on product pages.
          if (loader) loader.stopAll();
        }.bind(this));
    }

    changeLine(key, quantity) {
      this.setBusy(true);
      fetch(cartUrl('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity, sections: this.sectionId, sections_url: window.location.pathname })
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          this.renderSections(cart && cart.sections);
        }.bind(this))
        .catch(function () {})
        .finally(function () { this.setBusy(false); }.bind(this));
    }

    saveNote(value) {
      fetch(cartUrl('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value })
      }).catch(function () {});
    }

    /* ---- Discount codes ----
       Not the /discount/CODE permalink: that one navigates away (killing the
       drawer) and 302s to /cart whether or not the code stuck, so there is
       nothing to report back. POST /cart/update.js {discount} instead — it
       returns the cart with `discount_codes: [{code, applicable}]`, which tells
       us whether the code was actually accepted. Applying replaces the whole
       set, so every call posts the codes already on the cart plus the new one. */
    appliedCodes() {
      // Code rows render twice (discount sheet + desktop foot), so dedupe.
      var seen = {};
      return Array.prototype.slice.call(this.querySelectorAll('[data-discount-code]'))
        .map(function (el) { return el.dataset.discountCode; })
        .filter(function (code) {
          if (!code) return false;
          var key = code.toLowerCase();
          if (seen[key]) return false;
          seen[key] = true;
          return true;
        });
    }

    /* Codes are case-insensitive at checkout, so compare them that way too. */
    hasCode(list, code) {
      var needle = code.toLowerCase();
      return list.some(function (entry) { return String(entry).toLowerCase() === needle; });
    }

    /* One status line under the field, styled by `kind` ('error' | 'success').
       Success matters on its own: a free-shipping code produces no cart-level
       allocation, so no chip renders and this is the only confirmation. */
    discountFeedback(message, kind) {
      var box = this.querySelector('[data-cart-discount-feedback]');
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
      box.classList.toggle('cart-panel__feedback--error', !!message && kind !== 'success');
      box.classList.toggle('cart-panel__feedback--success', kind === 'success');
    }

    postDiscount(codes, withSections) {
      var body = { discount: codes.join(',') };
      if (withSections) {
        body.sections = this.sectionId;
        body.sections_url = window.location.pathname;
      }
      return fetch(cartUrl('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) { return res.json(); });
    }

    applyDiscount() {
      var input = this.querySelector('[data-cart-discount]');
      var panel = this.querySelector('[data-cart-panel="discount"]');
      if (!input || !panel) return;

      var messages = panel.dataset;
      var code = input.value.trim();
      if (!code) { this.discountFeedback(messages.errorEmpty); input.focus(); return; }

      var existing = this.appliedCodes();
      if (this.hasCode(existing, code)) { this.discountFeedback(messages.errorDuplicate); return; }

      var self = this;
      var button = this.querySelector('[data-cart-discount-apply]');
      this.discountFeedback('');
      this.setBusy(true);
      if (button) button.setAttribute('aria-disabled', 'true');

      this.postDiscount(existing.concat([code]), true)
        .then(function (cart) {
          var accepted = (cart.discount_codes || []).some(function (entry) {
            return String(entry.code).toLowerCase() === code.toLowerCase() && entry.applicable;
          });

          if (accepted) {
            input.value = '';
            // Re-render first (new totals + chips), then confirm in the fresh
            // markup and reopen the panel so the message is actually visible.
            self.renderSections(cart.sections);
            self.togglePanel('discount');
            self.discountFeedback(messages.successApplied, 'success');
            return;
          }

          // Shopify keeps a rejected code on the cart session, so put the
          // previously-applied set back before reporting the failure.
          return self.postDiscount(existing, false).then(function () {
            self.discountFeedback(messages.errorInvalid);
          });
        })
        .catch(function () { self.discountFeedback(messages.errorNetwork); })
        .finally(function () {
          self.setBusy(false);
          if (button) button.removeAttribute('aria-disabled');
        });
    }

    /* Removing one code = re-posting the others (an empty string clears all). */
    removeDiscount(code) {
      var self = this;
      var remaining = this.appliedCodes().filter(function (entry) {
        return String(entry).toLowerCase() !== String(code).toLowerCase();
      });

      this.setBusy(true);
      this.postDiscount(remaining, true)
        .then(function (cart) { self.renderSections(cart.sections); })
        .catch(function () {})
        .finally(function () { self.setBusy(false); });
    }

    /* ---- Re-render via the Section Rendering API ---- */
    renderSections(sections) {
      var html = sections && (sections[this.sectionId] || sections['cart-drawer']);
      if (html) {
        this.swap(html);
        this.broadcast();
        return;
      }
      // No sections payload (older API response) → fetch fresh markup.
      this.refresh();
    }

    refresh() {
      fetch(window.location.pathname + '?sections=' + this.sectionId, { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var html = data && (data[this.sectionId] || data['cart-drawer']);
          if (html) this.swap(html);
          this.broadcast();
        }.bind(this))
        .catch(function () {});
    }

    swap(html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = parsed.querySelector('[data-cart-content]');
      var current = this.querySelector('[data-cart-content]');
      if (fresh && current) current.replaceWith(fresh);
      this.setupGesture(); // the old <gesture-element> went with the old markup
    }

    broadcast() {
      var content = this.querySelector('[data-cart-content]');
      var count = content ? parseInt(content.dataset.itemCount, 10) || 0 : 0;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { item_count: count } }));
    }

    setBusy(state) {
      this.toggleAttribute('data-busy', !!state);
    }
  });
})();
